import { useMemo } from "react"
import {
  motion,
  MotionValue,
  useMotionTemplate,
  useTransform,
} from "motion/react"
import { cn } from "@/utils/cn"

type SlidingWheelProps = {
  icons: React.ReactNode[]
  progress: MotionValue<number>
  className?: string
  radiusX?: number
  radiusY?: number
}

export function SlidingWheel({
  icons,
  progress,
  className,
  radiusX = 100,
  radiusY = 300,
}: SlidingWheelProps) {
  const angleOffset = useMemo(
    () => (2 * Math.PI) / icons.length,
    [icons.length]
  )
  const rotation = useTransform(progress, [0, 1], [0, 360], {
    clamp: true,
  })

  return (
    <motion.div
      className={cn("relative", className)}
      style={{
        width: radiusX * 2,
        height: radiusY * 2,
      }}
    >
      {icons.map((icon, i) => (
        <Icon
          key={i}
          rotation={rotation}
          angleOffset={angleOffset}
          radiusX={radiusX}
          radiusY={radiusY}
          index={i}
        >
          {icon}
        </Icon>
      ))}
    </motion.div>
  )
}

function Icon({
  angleOffset,
  rotation,
  index,
  children,
  radiusX,
  radiusY,
}: {
  children: React.ReactNode
  index: number
  rotation: MotionValue<number>
  angleOffset: number
  radiusX: number
  radiusY: number
}) {
  const angle = useTransform(
    rotation,
    (v) => angleOffset * index + (v * Math.PI) / 180
  )
  const xPos = useTransform(angle, (a) => radiusX + radiusX * Math.sin(a))
  const yPos = useTransform(angle, (a) => radiusY + radiusY * -Math.cos(a))
  const scale = useTransform(
    angle,
    [
      Math.PI,
      (23 * Math.PI) / 16,
      (3 * Math.PI) / 2,
      (25 * Math.PI) / 16,
      2 * Math.PI,
    ],
    [1, 3, 3, 3, 1]
  )
  const blur = useTransform(scale, [1, 3], [10, 0])
  const opacity = useTransform(scale, [1, 3], [0.5, 1])

  return (
    <motion.div
      key={index}
      className="absolute"
      style={{
        translate: useMotionTemplate`calc(-50% + ${xPos}px) calc(-50% + ${yPos}px)`,
        scale,
        filter: useMotionTemplate`blur(${blur}px)`,
        opacity,
      }}
    >
      {children}
    </motion.div>
  )
}
